<script lang="ts">
	import { createEventDispatcher } from 'svelte';

	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import type { AccountError } from '$lib/account/account-error';
	import { signinByPassword } from '$lib/account/signin-by-password';
	import { signout } from '$lib/account/signout';
	import { clickOutside } from '$lib/click-outside';
	import { validator } from '@felte/validator-yup';
	import { createForm } from 'felte';
	import { slide } from 'svelte/transition';
	import { object, string, type InferType } from 'yup';
	import CustomInput from '../CustomInputs/CustomInput.svelte';
	import { useAccount } from '$lib/account/account';
	import ForgotPassword from './ForgotPassword.svelte';
	import { useBookingData } from '$lib/booking-data/booking-data';
	import { useClient } from '$lib/client/client';

	const { account } = useAccount();
	const { bookingData } = useBookingData();
	const { client } = useClient();

	const dispatch = createEventDispatcher<{
		closeRegister: void;
	}>();

	let accountError: undefined | AccountError;
	let showForgotPassword: boolean = false;
	let showChangePassword: boolean = false;

	const schema = object({
		email: string().email('Invalid email address.').required('Email address is required.'),
		password: string().required('Password is required.')
	});

	type Schema = InferType<typeof schema>;

	const { form, errors, data, isSubmitting, setFields } = createForm<Schema>({
		extend: validator({ schema }),
		initialValues: {
			email: '',
			password: ''
		},
		onSubmit: async (values) => {
			accountError = undefined;
			const result = await signinByPassword(values.email, values.password);

			if (result.code) {
				accountError = result;
				return;
			}
			if ($page.url.pathname.includes('registration')) {
				goto($page.url.pathname, { replaceState: true });
			}
			dispatch('closeRegister');
		}
	});

	//
	const _signout = async () => {
		let locationId = $bookingData.location_id;
		let view = $bookingData.featureOnCalendar ? 'calendar' : 'list';
		await signout();
		if ($page.url.pathname !== '/' && $page.url.pathname !== '/final') {
			goto(`/classes?location=${locationId}&view=${view}`, { replaceState: true });
		} else {
			goto('/', { replaceState: true });
		}
	};
</script>

<form
	transition:slide={{ duration: 300 }}
	class="register-modal"
	use:clickOutside={{
		onClick: () => {
			dispatch('closeRegister');
		}
	}}
>
	{#if $account}
		<div class="flex flex-col justify-center items-center gap-3">
			{#if $client}
				<div class="text-center">
					<p class="text-lg text-affair font-poppins-bold">
						{#if $client?.nameFirst}
							{$client?.nameFirst}
						{/if}
						{#if $client?.nameLast}
							{$client?.nameLast}
						{/if}
					</p>
				</div>
			{/if}
			<button type="submit" class="register-btn !bg-affair w-[150px]" on:click={_signout}>
				Sign Out
			</button>
		</div>
	{:else}
		<form class="flex flex-col gap-2" use:form>
			{#if !showForgotPassword}
				<h2 class="text-2xl font-jakarta-bold text-shark text-center">Log In</h2>
				<div>
					<CustomInput
						bind:value={$data.email}
						on:input={(e) => setFields('email', e.detail)}
						label={'Email Address'}
						placeholder={''}
					/>
					{#if $errors.email}
						<span class="text-red text-sm">{$errors.email.toString()}</span>
					{/if}
				</div>
				<div>
					<CustomInput
						bind:value={$data.password}
						on:input={(e) => setFields('password', e.detail)}
						label={'Password'}
						placeholder={''}
					/>
					{#if $errors.password}
						<span class="text-red text-sm">{$errors.password.toString()}</span>
					{/if}
				</div>
				<button
					type="button"
					on:click={(event) => ((showForgotPassword = true), event.stopPropagation())}
					class="text-sm text-affair cursor-pointer"
				>
					Forgot password
				</button>
				<div class="flex justify-center">
					<button type="submit" class="register-btn !bg-affair w-full" disabled={$isSubmitting}>
						{#if $isSubmitting}
							Please wait ...
						{:else}
							Sign In
						{/if}
					</button>
				</div>
			{:else}
				<ForgotPassword
					on:closeForgotPassword={() => (showForgotPassword = false)}
					on:closeRegister={() => dispatch('closeRegister')}
				/>
			{/if}

			{#if accountError}
				<div class="flex justify-center">
					<span class="text-red text-sm text-center">{accountError.message}</span>
				</div>
			{/if}
		</form>
	{/if}
</form>
